import {
  Suspense,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useLocation } from "react-router";
import { ErrorBoundary } from "react-error-boundary";
import { Notification } from "@/components/admin/notification";
import { Error } from "@/components/admin/error";
import { TableSkeleton } from "@/components/admin/loading-skeletons";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";

import { useConfigurationLoader } from "../root/useConfigurationLoader";
import { useEnsureOwnCompany } from "../root/useEnsureOwnCompany";
import Header from "./Header";
import { NextStepPrompt } from "../deals/NextStepPrompt";
import { CommandPalette } from "./CommandPalette";
import { KeyboardShortcuts } from "./KeyboardShortcuts";
import { PwaUpdateToast } from "./PwaUpdateToast";
import { SkipLink } from "./SkipLink";
import { TopBar } from "./TopBar";
import { TopProgressBar } from "./TopProgressBar";

export const Layout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  useEnsureOwnCompany();
  const mainRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const { pathname } = location;
  // Hovedfeltet er sin egen rullecontainer og blir stående mellom sidebytter,
  // så rulleposisjonen fulgte med til neste side (f.eks. midt i kundelista
  // etter å ha arkivert en kunde nederst på kortet). Bare stien teller:
  // filter og sortering i søkestrengen skal ikke hoppe til toppen.
  //
  // Unntak: dialoger med egen rute skal ikke flytte siden bak. Under /deals
  // er visning, oppretting og redigering dialoger over tavla, så hele
  // /deals regnes som én side. Andre steder bruker slike dialoger ra-cores
  // `_scrollToTop: false`.
  const pageKey = pathname.startsWith("/deals") ? "/deals" : pathname;
  const keepScroll =
    (location.state as { _scrollToTop?: boolean } | null)?._scrollToTop ===
    false;
  useLayoutEffect(() => {
    if (keepScroll) return;
    mainRef.current?.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);
  return (
    // Full-bredde topp-bar øverst; ikon-rail + hovedfelt under.
    // --mimin-topbar-h gir sidebarens fixed-container et topp-offset så den
    // starter under topp-baren (se ui/sidebar.tsx).
    <div
      className="flex h-svh flex-col overflow-hidden"
      style={{ "--mimin-topbar-h": "3rem" } as CSSProperties}
    >
      <SkipLink />
      <TopProgressBar />
      <TopBar />
      <SidebarProvider
        className="!min-h-0 flex-1 overflow-hidden"
        style={{ "--sidebar-width-icon": "4.25rem" } as CSSProperties}
      >
        <Header />
        <SidebarInset className="min-h-0 overflow-hidden">
          <header className="flex h-12 shrink-0 items-center gap-2 border-b px-3 md:hidden">
            <SidebarTrigger />
          </header>
          <CommandPalette />
          <KeyboardShortcuts />
          <NextStepPrompt />
          <PwaUpdateToast />
          <div className="flex min-h-0 flex-1">
            <main
              ref={mainRef}
              // --page-gutter er sidemargen. Klistrete elementer (FormActions,
              // Kundeinfo) trekker den fra, så den må bare endres her.
              className="flex-1 overflow-auto p-(--page-gutter) outline-none [--page-gutter:1rem] md:[--page-gutter:1.5rem]"
              id="main-content"
              tabIndex={-1}
            >
              {/* Innholdskolonnen: maks sidebredden («page», 96 rem) og
                  sentrert, så ultrabrede skjermer ikke får alt innhold mot
                  venstre kant. Sidene venstrejusteres inne i kolonnen, så
                  tittelen står på samme sted uansett sidetype. */}
              <div className="mx-auto w-full max-w-page">
                <ErrorBoundary FallbackComponent={Error}>
                  <Suspense fallback={<PageFallback />}>{children}</Suspense>
                </ErrorBoundary>
              </div>
            </main>
          </div>
          <Notification />
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
};

// Vises mens koden til en side lastes ned første gang. En generisk side med
// tittel og tabell ligner nok på de fleste sidene til at overgangen er rolig.
const PageFallback = () => (
  <div className="flex flex-col gap-4">
    <Skeleton className="mt-1 h-7 w-40" />
    <TableSkeleton rows={6} columns={4} />
  </div>
);
